import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Stack,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useToast } from '../../hooks/useToast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const STATUS_META = {
  completed: { color: 'success', label: 'Completed' },
  pending:   { color: 'warning', label: 'Pending' },
  failed:    { color: 'error',   label: 'Failed' },
  refunded:  { color: 'info',    label: 'Refunded' },
};

const getStatusMeta = (status) =>
  STATUS_META[status?.toLowerCase()] ?? { color: 'default', label: status || 'N/A' };

const AdminTransactions = ({
  transactions,
  transactionsLoading,
  transactionsError,
  transactionsPage,
  transactionsTotalPages,
  showTransactionModal,
  selectedTransaction,
  openTransactionModal,
  closeTransactionModal,
  fetchTransactions,
}) => {
  const toast = useToast();

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/transactions/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to export transactions');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'transactions.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed: ' + err.message);
    }
  };

  const handlePageChange = (_, page) => fetchTransactions(page);

  return (
    <>
      <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
        <CardHeader
          title="Transactions"
          subheader="View and audit all financial transactions in the system"
          titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
          action={
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExportCSV}
              size="small"
              sx={{ mt: 1, mr: 1 }}
            >
              Export CSV
            </Button>
          }
        />
        {transactionsLoading && <LinearProgress color="primary" />}
        <CardContent sx={{ pt: 0 }}>
          {transactionsError && (
            <Alert severity="error" sx={{ mb: 2 }}>{transactionsError}</Alert>
          )}

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 620 }}>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!transactions || transactions.length === 0) && !transactionsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                        No transactions found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (transactions || []).map((tx, index) => {
                    const statusMeta = getStatusMeta(tx.status);
                    return (
                      <TableRow
                        key={tx._id || `${tx.type}-${tx.status}-${index}`}
                        hover
                        sx={{ '&:last-child td': { border: 0 } }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                            {tx._id || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {tx.userId?.username || tx.user?.username || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {tx.tokenAmount ?? tx.amount ?? tx.totalAmount ?? 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusMeta.label}
                            color={statusMeta.color}
                            size="small"
                            sx={{ fontWeight: 600, borderRadius: 1.5 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {tx.timestamp
                              ? new Date(tx.timestamp).toLocaleDateString()
                              : tx.createdAt
                              ? new Date(tx.createdAt).toLocaleDateString()
                              : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="text" onClick={() => openTransactionModal(tx)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {transactionsTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={transactionsTotalPages}
                page={transactionsPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Transaction Details Dialog */}
      <Dialog
        open={!!(showTransactionModal && selectedTransaction)}
        onClose={closeTransactionModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ReceiptLongOutlinedIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>Transaction Details</Typography>
          </Stack>
          <IconButton size="small" onClick={closeTransactionModal}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        {selectedTransaction && (
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              {[
                { label: 'Transaction ID', value: selectedTransaction._id || selectedTransaction.transactionId || 'N/A', mono: true },
                { label: 'User', value: selectedTransaction.userId?.username || selectedTransaction.user?.username || 'N/A' },
                { label: 'Amount', value: String(selectedTransaction.tokenAmount ?? selectedTransaction.amount ?? selectedTransaction.totalAmount ?? 'N/A') },
                { label: 'Status', value: selectedTransaction.status, isStatus: true },
                { label: 'Date', value: selectedTransaction.createdAt ? new Date(selectedTransaction.createdAt).toLocaleString() : 'N/A' },
                ...(selectedTransaction.description ? [{ label: 'Description', value: selectedTransaction.description }] : []),
              ].map(({ label, value, mono, isStatus }) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.05em">
                    {label}
                  </Typography>
                  <Box sx={{ mt: 0.25 }}>
                    {isStatus ? (
                      <Chip
                        label={getStatusMeta(value).label}
                        color={getStatusMeta(value).color}
                        size="small"
                        sx={{ fontWeight: 600, borderRadius: 1.5 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? '0.75rem' : undefined }}>
                        {value}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={closeTransactionModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminTransactions;
